import { useEffect, useRef } from 'react'
import {
  matchesTaskKey,
  taskMenuTargetForKey,
  TASK_MENU_DETAILS_KEY,
  TASK_MENU_FAVORITE_KEY,
  TASK_MENU_FOCUS_KEYS,
  TASK_MENU_REST_KEYS,
  TASK_MENU_SHOW_IN_PROJECT_KEY,
} from '../components/TaskMenu/taskMenuShortcuts'
import type { TaskMenuTarget } from '../components/TaskMenu/TaskMenu.types'
import { isInsideTaskDetails } from '../utils/taskDetailsFocus'

interface UseTaskMenuShortcutsParams {
  /** Aus, solange die Seite nicht die aktive ist (TabStack hält sie montiert). */
  enabled?: boolean
  /**
   * Welche Aufgabe gemeint ist — die Zeile unter dem Tastaturfokus, sonst die
   * oberste ausgewählte. Gemeint ist damit nur der ANKER; geändert wird die
   * ganze Auswahl, das entscheidet der optimistische Kanal des Aufrufers.
   */
  getAnchorTaskId: () => string | null
  /** `S`, `E`, `D`, `T`, ⇧⌘R, ⇧⌘A, ⇧⌘G … — dasselbe öffnen wie die Menüzeile (`TASK_MENU_KEYS`). */
  onMenuShortcut?: (taskId: string, target: TaskMenuTarget) => void
  /** ⇧⌘L — ruhen lassen oder wecken. */
  onRestShortcut?: (taskId: string) => void
  /** **`P`lay** (oder ⌘P) — beginnt einen **Fokus** an der Anker-Aufgabe. */
  onFocusShortcut?: (taskId: string) => void
  /**
   * **⇧⌘F** — der Stern (#210). `null` heißt: keine Aufgabe gemeint; die Projektseite meint dann
   * das Projekt.
   */
  onFavoriteShortcut?: (taskId: string | null) => void
  /** **⌘I** — die Aufgaben-Details auf und zu (#257). */
  onDetailsShortcut?: () => void
  /** **⌘J** — Im Projekt zeigen (#257). */
  onShowInProjectShortcut?: (taskId: string) => void
  /** **⇧⌘M** (#257) — die Seite stellt „Verschieben …“ bereit; dann öffnet die Taste es an der Zeile. */
  canMove?: boolean
}

/**
 * **Die Tastenkürzel des Aufgaben-Menüs**, für jede Seite mit einer
 * Aufgabenliste — welche Taste was tut, steht in `taskMenuShortcuts.ts`.
 *
 * Sie stehen hier und nicht in `useProjectKeyboardShortcuts`, weil sie für
 * mehr Seiten gelten als dessen übrige Tasten: `A`/`B`/`F` legen eine neue
 * Aufgabe an — auf **Ruhend** gibt es nichts anzulegen, wohl aber eine Aufgabe
 * zu bearbeiten. Die ⌘-Kürzel zeigt das Menü „Aufgaben“ der Menüleiste nur an
 * (#257); gebunden sind auch sie hier, damit sie im Web genauso gehen.
 *
 * Die Wächter, und jeder hat seinen Grund:
 *
 * - **Ein offenes Menü** bekommt die Tasten selbst (`TaskMenu` fängt sie ab,
 *   bevor Radix' Typeahead sie als Sprungmarke nimmt).
 * - **Ein Eingabefeld** tippt Buchstaben, es öffnet nichts — und behält seine
 *   ⌘-Kürzel (⌘I kursiv, ⇧⌘S durchgestrichen im Titel-Editor).
 * - **Die Aufgaben-Details** behalten ihre Tasten, bis auf Stern (⇧⌘F) und
 *   Details (⌘I) — die nennt ihr Kopf selbst.
 */
export function useTaskMenuShortcuts({
  enabled = true,
  getAnchorTaskId,
  onMenuShortcut,
  onRestShortcut,
  onFocusShortcut,
  onFavoriteShortcut,
  onDetailsShortcut,
  onShowInProjectShortcut,
  canMove = false,
}: UseTaskMenuShortcutsParams) {
  const latest = useRef({ getAnchorTaskId, onMenuShortcut, onRestShortcut, onFocusShortcut, onFavoriteShortcut, onDetailsShortcut, onShowInProjectShortcut, canMove })
  latest.current = { getAnchorTaskId, onMenuShortcut, onRestShortcut, onFocusShortcut, onFavoriteShortcut, onDetailsShortcut, onShowInProjectShortcut, canMove }

  useEffect(() => {
    if (!enabled) return
    const handle = (e: KeyboardEvent) => {
      if (document.querySelector('[role="menu"]')) return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if ((e.target as HTMLElement)?.isContentEditable) return
      const p = latest.current

      // Stern und Details auch aus den Aufgaben-Details: Beide nennen ihr Kürzel dort selbst (#210).
      if (matchesTaskKey(e, TASK_MENU_FAVORITE_KEY)) {
        if (!p.onFavoriteShortcut) return
        e.preventDefault()
        p.onFavoriteShortcut(p.getAnchorTaskId())
        return
      }
      if (matchesTaskKey(e, TASK_MENU_DETAILS_KEY)) {
        if (!p.onDetailsShortcut) return
        e.preventDefault()
        p.onDetailsShortcut()
        return
      }

      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Dort
      // steht der Fokus meist auf einem Knopf, an dem der INPUT/TEXTAREA-Wächter
      // oben vorbeigeht.
      if (isInsideTaskDetails(e.target)) return

      const target = taskMenuTargetForKey(e)
      const act: ((taskId: string) => void) | undefined =
        target ? (target === 'move' && !p.canMove ? undefined : p.onMenuShortcut && (taskId => p.onMenuShortcut!(taskId, target)))
        : TASK_MENU_REST_KEYS.some(key => matchesTaskKey(e, key)) ? p.onRestShortcut
        : TASK_MENU_FOCUS_KEYS.some(key => matchesTaskKey(e, key)) ? p.onFocusShortcut
        : matchesTaskKey(e, TASK_MENU_SHOW_IN_PROJECT_KEY) ? p.onShowInProjectShortcut
        : undefined
      if (!act) return

      const taskId = p.getAnchorTaskId()
      if (!taskId) return
      e.preventDefault()
      act(taskId)
    }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [enabled])
}

/**
 * **Welche Aufgabe ein Kürzel meint.**
 *
 * Zuerst die Zeile unter dem Tastaturfokus — wer mit den Pfeilen gefahren ist,
 * meint die. Sonst die OBERSTE der ausgewählten: Ein Mausklick wählt aus, ohne
 * einen Fokus zu setzen, und ohne diesen zweiten Weg täte `W` an einer sichtbar
 * markierten Aufgabe nichts (gemeldet 2026-09-11).
 */
export function anchorTaskId(
  focusedTaskId: string | null,
  orderedTaskIds: readonly string[],
  selection: Set<string> | undefined,
): string | null {
  if (focusedTaskId) return focusedTaskId
  if (!selection || selection.size === 0) return null
  return orderedTaskIds.find(id => selection.has(id)) ?? null
}
